import { useCallback, useEffect, useRef, useState } from 'react';

export type CardNode<TData> = {
    data: TData;
    id: string;
    rowIndex: number;
    isSelected: () => boolean;
    setSelected: (selected: boolean) => void;
};

export type CardSelectionApi<TData> = {
    selectedIndices: ReadonlySet<number>;
    isSelected: (index: number) => boolean;
    isSelectable: (row: TData) => boolean;
    toggleSelection: (row: TData, index: number) => void;
    selectAll: () => void;
    deselectAll: () => void;
    getSelectedNodes: () => CardNode<TData>[];
    forEachNode: (callback: (node: CardNode<TData>) => void) => void;
};

type UseCardSelectionOptions<TData> = {
    rowSelection: 'single' | 'multiple' | undefined;
    isRowSelectable?: (params: { data: TData | undefined }) => boolean;
    rows: TData[];
    onSelectionChanged?: () => void;
};

export function useCardSelection<TData>({
    rowSelection,
    isRowSelectable,
    rows,
    onSelectionChanged,
}: UseCardSelectionOptions<TData>): CardSelectionApi<TData> {
    const [selectedIndices, setSelectedIndices] = useState<Set<number>>(new Set());

    // Keep a ref so CardNode.setSelected can access the latest setter without stale closures
    const setSelectedRef = useRef(setSelectedIndices);
    setSelectedRef.current = setSelectedIndices;

    const onSelectionChangedRef = useRef(onSelectionChanged);
    onSelectionChangedRef.current = onSelectionChanged;

    const rowsRef = useRef(rows);
    rowsRef.current = rows;

    // Fire onSelectionChanged after state settles
    const prevSelectedRef = useRef(selectedIndices);
    useEffect(() => {
        if (prevSelectedRef.current !== selectedIndices) {
            prevSelectedRef.current = selectedIndices;
            onSelectionChangedRef.current?.();
        }
    }, [selectedIndices]);

    const isSelectable = useCallback(
        (row: TData): boolean => {
            if (!isRowSelectable) {
                return true;
            }
            return isRowSelectable({ data: row });
        },
        [isRowSelectable],
    );

    const toggleSelection = useCallback(
        (row: TData, index: number) => {
            if (!isSelectable(row)) {
                return;
            }

            setSelectedIndices((prev) => {
                const next = new Set(prev);

                if (rowSelection === 'multiple') {
                    if (next.has(index)) {
                        next.delete(index);
                    } else {
                        next.add(index);
                    }
                } else {
                    // Single select — toggle or replace
                    if (next.has(index)) {
                        next.clear();
                    } else {
                        next.clear();
                        next.add(index);
                    }
                }

                return next;
            });
        },
        [rowSelection, isSelectable],
    );

    const selectAll = useCallback(() => {
        if (rowSelection !== 'multiple') {
            return;
        }

        setSelectedIndices(() => {
            const next = new Set<number>();
            const currentRows = rowsRef.current;
            for (let i = 0; i < currentRows.length; i++) {
                if (!isRowSelectable || isRowSelectable({ data: currentRows[i] })) {
                    next.add(i);
                }
            }
            return next;
        });
    }, [rowSelection, isRowSelectable]);

    const deselectAll = useCallback(() => {
        setSelectedIndices(new Set());
    }, []);

    const isSelected = useCallback(
        (index: number): boolean => {
            return selectedIndices.has(index);
        },
        [selectedIndices],
    );

    const makeNode = useCallback(
        (row: TData, index: number): CardNode<TData> => {
            return {
                data: row,
                id: String(index),
                rowIndex: index,
                isSelected: () => selectedIndices.has(index),
                setSelected: (selected: boolean) => {
                    setSelectedRef.current((prev) => {
                        const next = new Set(prev);
                        if (selected) {
                            next.add(index);
                        } else {
                            next.delete(index);
                        }
                        return next;
                    });
                },
            };
        },
        [selectedIndices],
    );

    const getSelectedNodes = useCallback((): CardNode<TData>[] => {
        const currentRows = rowsRef.current;
        const nodes: CardNode<TData>[] = [];
        for (const idx of selectedIndices) {
            if (idx < currentRows.length) {
                nodes.push(makeNode(currentRows[idx], idx));
            }
        }
        return nodes;
    }, [selectedIndices, makeNode]);

    const forEachNode = useCallback(
        (callback: (node: CardNode<TData>) => void) => {
            const currentRows = rowsRef.current;
            for (let i = 0; i < currentRows.length; i++) {
                callback(makeNode(currentRows[i], i));
            }
        },
        [makeNode],
    );

    return {
        selectedIndices,
        isSelected,
        isSelectable,
        toggleSelection,
        selectAll,
        deselectAll,
        getSelectedNodes,
        forEachNode,
    };
}
